import React, { useState } from "react"
import { FileText, Loader2, Share2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { useGenerateQuotation } from "../hooks/useDocumentSettings"
import { ShareDocumentDrawer } from "./ShareDocumentDrawer"

// Generates a branded quotation / cost-sheet PDF and opens it inline in a new
// tab (where the user can review and print). Once generated, exposes a Share
// action that opens the standard ShareDocumentDrawer for the saved document.
// Pass either a `bookingId` (post-booking) or a `dealId` + `unitId`
// (pre-booking quote).
export default function QuotationButton({
  bookingId,
  dealId,
  unitId,
  costSheet,
  variant = "outline",
  size = "sm",
  label = "Quotation",
  className = "gap-1.5",
  onGenerated,
}) {
  const { mutate, isPending } = useGenerateQuotation()
  const { toast } = useToast()
  const [generatedDoc, setGeneratedDoc] = useState(null)
  const [shareOpen, setShareOpen] = useState(false)

  async function handleGenerate() {
    const payload = bookingId
      ? { booking_id: bookingId }
      : { deal_id: dealId, unit_id: unitId, cost_sheet: costSheet }

    try {
      const result = await mutate(payload)
      setGeneratedDoc(result?.document || null)
      toast({ title: "Quotation generated", description: "The cost sheet PDF is ready." })
      onGenerated?.(result)
      if (result?.view_url) window.open(result.view_url, "_blank")
    } catch (errors) {
      toast({
        variant: "destructive",
        title: "Error",
        description: errors?.[0] || "Failed to generate quotation",
      })
    }
  }

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={handleGenerate} disabled={isPending}>
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        {label}
      </Button>
      {generatedDoc && (
        <Button variant="ghost" size={size} className="gap-1.5" onClick={() => setShareOpen(true)} title="Share quotation">
          <Share2 className="h-4 w-4" /> Share
        </Button>
      )}
      <ShareDocumentDrawer document={generatedDoc} open={shareOpen} onOpenChange={setShareOpen} />
    </>
  )
}
